import React, { FC } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form/dist/index.ie11';

import { FieldMessage } from '@blueprint/ui';

import registrationAndSignInTranslations from 'public/locales/en/registration-and-sign-in.json';
import { getTranslator } from 'shared/i18n/getTranslator';

import {
    FieldContainer,
    FormSection,
    ReassuranceText,
    RegisterButton
} from './../AuthModal.styles';
import { RadioGroup, RadioButton } from 'shared/components';
import PreferencesFormData from './../interfaces/PreferencesFormData';
import useUpdatePreferences from '../hooks/useUpdatePreferences';
import { useUser } from 'shared/hooks/useUser';
import { trackAuthSegmentEvent } from '../utils/segmentTrack';
import { PreferencesProps } from './Preferences.types';
import useUpdateAboutMe from '../hooks/useUpdateAboutMe';
import { trackAboutMe, trackOptin, trackSubmit } from './Preferences.tracking';
import { AboutYou } from '../common/AboutYou';

const translate = getTranslator(registrationAndSignInTranslations);
const serviceError = translate('serviceError');
const termsUnsubscribe = translate('terms.youMayUnsubscribe');
const saveButton = translate('saveButton');

const PreferencesForm: FC<PreferencesProps> = ({
    close,
    actionAfterAuth,
    isSSOPhaseTwoFeatureEnabled
}) => {
    const {
        register,
        handleSubmit,
        errors,
        trigger
    } = useForm<PreferencesFormData>({
        mode: 'onBlur',
        reValidateMode: 'onBlur'
    });
    const user = useUser();

    const { updatePreferences, generalError } = useUpdatePreferences(close);
    const {
        updateAboutMe,
        generalError: aboutMeGeneralError
    } = useUpdateAboutMe(close);

    const onFormSubmit: SubmitHandler<PreferencesFormData> = async (
        formData
    ) => {
        const payload = await updatePreferences(formData);
        const { consent__insights__24, situation } = formData;
        if (!payload?.data?.updateUserConsents) {
            return;
        }

        if (consent__insights__24 !== undefined) {
            trackOptin(consent__insights__24, actionAfterAuth?.eventLabel);

            if (consent__insights__24) {
                trackAuthSegmentEvent('optin', actionAfterAuth?.segment, user);
            }
        }

        if (situation) {
            updateAboutMe(formData);
        }
        trackAboutMe(situation);

        trackSubmit(actionAfterAuth?.eventLabel);

        close();
    };

    const onSubmit = handleSubmit(onFormSubmit);

    return (
        <>
            <form
                onSubmit={onSubmit}
                noValidate
                data-testid="auth-modal-preferences-form"
            >
                {(generalError || aboutMeGeneralError) && (
                    <FieldMessage
                        data-testid="general-error"
                        variant="invalid"
                        message={serviceError}
                    />
                )}

                {isSSOPhaseTwoFeatureEnabled ? (
                    <FormSection>
                        <FieldContainer>
                            <AboutYou dropDownRef={register} />
                        </FieldContainer>
                    </FormSection>
                ) : null}

                <FormSection>
                    <RadioGroup
                        legend={translate('emailPreferences.legend')}
                        description={translate('emailPreferences.description')}
                        radioGroupName="consent__insights__24"
                        errorMessage={
                            errors.consent__insights__24 &&
                            translate('emailPreferences.errorMessage')
                        }
                        onClick={() => trigger('consent__insights__24')}
                    >
                        <RadioButton
                            value="true"
                            id="yes"
                            activeTextColor="white"
                            label={translate('emailPreferences.yes')}
                            ref={register({ required: true })}
                        />
                        <RadioButton
                            value=""
                            id="no"
                            label={translate('emailPreferences.no')}
                            activeTextColor="white"
                            activeBackgroundColor="function-error"
                            ref={register({ required: true })}
                        />
                    </RadioGroup>
                </FormSection>
                <ReassuranceText
                    size="secondary"
                    color="grey-slate"
                    isBold
                    data-testid="unsubscribe-text"
                >
                    {termsUnsubscribe}
                </ReassuranceText>

                <RegisterButton
                    variant="primary"
                    isFullWidth
                    data-testid="update-preferences-button"
                >
                    {saveButton}
                </RegisterButton>
            </form>
        </>
    );
};

export default PreferencesForm;
